import { fg } from '@atlaskit/platform-feature-flags';

import {
	getPreferences,
	getPreferencesMaybeSync,
	// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
} from '../../../controllers/preferences-controls/get-preferences';
import {
	type CategorizedStorageControlsCache,
	type CategoryStorageType,
	type ConsentPreference,
	type FlatCategorizedStorageControlsCache,
	type FlatPattern,
	PreferenceCategory,
} from '../../../types';
import { getBscGlobalState } from '../bsc-global-state/core';
import { Logger } from '../logger';

/**
 * WeakMap cache for precompiled RegExp objects, keyed by the raw regExp product map.
 * This ensures RegExp strings are compiled at most once per unique cache object.
 */
const compiledRegExpCache = new WeakMap<
	{ [regexString: string]: number },
	Array<{ regexp: RegExp; category: number }>
>();

const compileRegExpPatterns = (rawPatterns: {
	[regexString: string]: number;
}): Array<{ regexp: RegExp; category: number }> => {
	if (compiledRegExpCache.has(rawPatterns)) {
		return compiledRegExpCache.get(rawPatterns)!;
	}

	const compiled: Array<{ regexp: RegExp; category: number }> = [];
	for (const [regexString, category] of Object.entries(rawPatterns)) {
		try {
			compiled.push({ regexp: new RegExp(regexString), category });
		} catch (e) {
			Logger.warn(
				`BSC: Invalid RegExp pattern "${regexString}" in storage controls cache — skipping.`,
			);
		}
	}

	compiledRegExpCache.set(rawPatterns, compiled);
	return compiled;
};

export const findStartsWithPatternMatch = (
	itemKey: string,
	startsWith: CategorizedStorageControlsCache['patterns']['startsWith'][string],
): number | null => {
	// Setup initial pattern step and position
	let currentStep = startsWith;
	let position = 0;

	// Iterate through item key symbol by symbol
	while (position < itemKey.length) {
		// The value of item character at the position
		const currentStepValue = currentStep[itemKey[position]];
		// If there is no match for item symbol in cache, this means there is no
		// pattern match for the item key
		if (currentStepValue === undefined) {
			return null;
		}
		// If current step value is a number, this means we reached the end of the pattern
		// and should return category number
		if (typeof currentStepValue === 'number') {
			return currentStepValue;
		}
		// Continue to going through pattern
		currentStep = currentStepValue;
		position++;
	}

	// No pattern match - all patterns longer or shorter than item key
	return null;
};

export const findRegExpPatternMatch = (
	itemKey: string,
	compiledPatterns: Array<{ regexp: RegExp; category: number }>,
): number | null => {
	for (const { regexp, category } of compiledPatterns) {
		if (regexp.test(itemKey)) {
			return category;
		}
	}
	return null;
};

export const findEndsWithPatternMatch = (
	itemKey: string,
	endsWith: CategorizedStorageControlsCache['patterns']['endsWith'][string],
): number | null => {
	// Return category number if str is found in the trie, otherwise return undefined
	const trieSearch = (str: string): number | undefined => {
		let currentStep: typeof endsWith | number = endsWith;
		let foundString = '';
		let terminalNumber: number | undefined;

		for (let i = 0; i < str.length; i++) {
			const char = str[i];

			currentStep = currentStep[char];
			if (currentStep === undefined) {
				break;
			}

			foundString += char;

			if (typeof currentStep === 'number') {
				terminalNumber = currentStep;
				break;
			}
		}

		if (foundString === str) {
			// Trie contained the complete string
			return terminalNumber;
		} else {
			// Trie did not contain the complete string
			return undefined;
		}
	};

	let position = 0;

	// Iterate through item key symbol by symbol
	while (position < itemKey.length) {
		const remainderMatch = trieSearch(itemKey.slice(position));
		if (remainderMatch !== undefined) {
			return remainderMatch;
		}
		position++;
	}

	return null;
};

/**
 * Matches an item key against a flat prefix map (new format).
 * Returns the category number if any prefix matches, or null.
 */
export const findStartsWithFlatPatternMatch = (
	itemKey: string,
	startsWith: FlatPattern,
): number | null => {
	for (const prefix of Object.keys(startsWith)) {
		if (itemKey.startsWith(prefix)) {
			return startsWith[prefix];
		}
	}
	return null;
};

/**
 * Matches an item key against a flat prefix map and returns the longest matching prefix.
 * Returns an object with the category and match length, or null if no prefix matches.
 * When multiple prefixes match, the longest one wins.
 */
export const findStartsWithLongestPatternMatch = (
	itemKey: string,
	startsWith: FlatPattern,
): { category: number; length: number } | null => {
	let best: { category: number; length: number } | null = null;
	for (const prefix of Object.keys(startsWith)) {
		if (itemKey.startsWith(prefix)) {
			if (best === null || prefix.length > best.length) {
				best = { category: startsWith[prefix], length: prefix.length };
			}
		}
	}
	return best;
};

/**
 * Matches an item key against a flat suffix map and returns the longest matching suffix.
 * Returns an object with the category and match length, or null if no suffix matches.
 * When multiple suffixes match, the longest one wins.
 */
export const findEndsWithLongestPatternMatch = (
	itemKey: string,
	endsWith: FlatPattern,
): { category: number; length: number } | null => {
	let best: { category: number; length: number } | null = null;
	for (const suffix of Object.keys(endsWith)) {
		if (itemKey.endsWith(suffix)) {
			if (best === null || suffix.length > best.length) {
				best = { category: endsWith[suffix], length: suffix.length };
			}
		}
	}
	return best;
};

/**
 * Matches an item key against a flat suffix map (new format).
 * Returns the category number if any suffix matches, or null.
 */
export const findEndsWithFlatPatternMatch = (
	itemKey: string,
	endsWith: FlatPattern,
): number | null => {
	for (const suffix of Object.keys(endsWith)) {
		if (itemKey.endsWith(suffix)) {
			return endsWith[suffix];
		}
	}
	return null;
};

/**
 * Synchronously fetches a an item/cookie category from a CategorizedStorageControlsCache that has already been fetched
 *
 * This allows synchronous parts of the code to access cached versions of the cache but still maintain
 * a synchronous signature for clients
 *
 */
export const getItemCategoryFromPassedCache = (
	itemKey: string,
	storageControlsData: CategorizedStorageControlsCache | FlatCategorizedStorageControlsCache,
	productName?: string,
): PreferenceCategory | null => {
	const downcasedProduct = productName?.toLowerCase();

	let itemCategoryNumber: number | null = null;
	const { categories, patterns } = storageControlsData;

	let productKeys = {
		...storageControlsData['keys']['all'],
		...(downcasedProduct ? storageControlsData['keys'][downcasedProduct] : {}),
	};

	const productStartsWithPatterns = {
		...patterns.startsWith['all'],
		...(downcasedProduct ? patterns.startsWith[downcasedProduct] : {}),
	};
	const productEndsWithPatterns = {
		...patterns.endsWith['all'],
		...(downcasedProduct ? patterns.endsWith[downcasedProduct] : {}),
	};

	// Find direct match in keys
	if (Object.keys(productKeys).includes(itemKey)) {
		itemCategoryNumber = productKeys[itemKey];
	} else if (fg('platform_bsc_flat_pattern_file_structure')) {
		if (fg('platform_bsc_longest_pattern_match_wins')) {
			// Longest-match-wins flat path
			const swMatch = findStartsWithLongestPatternMatch(
				itemKey,
				productStartsWithPatterns as FlatPattern,
			);

			if (swMatch !== null) {
				itemCategoryNumber = swMatch.category;
			} else {
				const ewMatch = findEndsWithLongestPatternMatch(
					itemKey,
					productEndsWithPatterns as FlatPattern,
				);
				if (ewMatch !== null) {
					itemCategoryNumber = ewMatch.category;
				}
			}
		} else {
			// First-match flat path (COOK-435)
			itemCategoryNumber = findStartsWithFlatPatternMatch(
				itemKey,
				productStartsWithPatterns as FlatPattern,
			);

			if (itemCategoryNumber === null) {
				itemCategoryNumber = findEndsWithFlatPatternMatch(
					itemKey,
					productEndsWithPatterns as FlatPattern,
				);
			}
		}

		// 3. RegExp fallback for both flat paths
		if (itemCategoryNumber === null && fg('platform_moonjelly_bsc_regexp_pattern_matching')) {
			const rawRegExpPatterns = {
				...patterns.regexp?.['all'],
				...(downcasedProduct ? patterns.regexp?.[downcasedProduct] : {}),
			};
			const compiledPatterns = compileRegExpPatterns(rawRegExpPatterns);
			itemCategoryNumber = findRegExpPatternMatch(itemKey, compiledPatterns);
		}
	} else {
		// Legacy trie matching path. When platform_bsc_lookup_product_then_all_pattern_tries
		// is ON we search the product trie first and fall back to the 'all' trie;
		// a merged trie is unsafe because a shared first character silently
		// drops the entire 'all' subtree under it (HOT-303432).
		if (fg('platform_bsc_lookup_product_then_all_pattern_tries')) {
			const productStartsWith = downcasedProduct
				? patterns.startsWith[downcasedProduct]
				: undefined;
			const productEndsWith = downcasedProduct ? patterns.endsWith[downcasedProduct] : undefined;
			const allStartsWith = patterns.startsWith['all'];
			const allEndsWith = patterns.endsWith['all'];

			if (productStartsWith) {
				itemCategoryNumber = findStartsWithPatternMatch(itemKey, productStartsWith);
			}
			if (itemCategoryNumber === null && allStartsWith) {
				itemCategoryNumber = findStartsWithPatternMatch(itemKey, allStartsWith);
			}
			if (itemCategoryNumber === null && productEndsWith) {
				itemCategoryNumber = findEndsWithPatternMatch(itemKey, productEndsWith);
			}
			if (itemCategoryNumber === null && allEndsWith) {
				itemCategoryNumber = findEndsWithPatternMatch(itemKey, allEndsWith);
			}
		} else {
			itemCategoryNumber = findStartsWithPatternMatch(itemKey, productStartsWithPatterns);

			if (itemCategoryNumber === null) {
				itemCategoryNumber = findEndsWithPatternMatch(itemKey, productEndsWithPatterns);
			}
		}

		if (itemCategoryNumber === null && fg('platform_moonjelly_bsc_regexp_pattern_matching')) {
			const rawRegExpPatterns = {
				...patterns.regexp?.['all'],
				...(downcasedProduct ? patterns.regexp?.[downcasedProduct] : {}),
			};
			const compiledPatterns = compileRegExpPatterns(rawRegExpPatterns);
			itemCategoryNumber = findRegExpPatternMatch(itemKey, compiledPatterns);
		}
	}

	const numToCategoryMap = {
		[categories.STRICTLY_NECESSARY]: PreferenceCategory.StrictlyNecessary,
		[categories.FUNCTIONAL]: PreferenceCategory.Functional,
		[categories.ANALYTICS]: PreferenceCategory.Analytics,
		[categories.MARKETING]: PreferenceCategory.Marketing,
		[categories.UNKNOWN]: PreferenceCategory.Unknown,
	};

	return itemCategoryNumber !== null ? numToCategoryMap[itemCategoryNumber] : null;
};

export const getItemCategory = async (
	itemKey: string,
	storageType: CategoryStorageType,
	productName?: string,
): Promise<PreferenceCategory | null> => {
	const storageControlsData = await getBscGlobalState().loadCache(storageType);

	if (!storageControlsData) {
		return null;
	} else {
		return getItemCategoryFromPassedCache(itemKey, storageControlsData, productName);
	}
};

const isPromise = <T>(value: unknown): value is Promise<T> =>
	value != null && typeof (value as { then?: unknown }).then === 'function';

interface CategoryAndPreference {
	itemCategory: PreferenceCategory | null;
	shouldProcessItem: boolean;
}

/**
 * Gets the item categories the the cache - if the item categories have not been loaded it will fetch them and return a promise.
 *
 * DO NOT CONVERT THIS FUNCTION TO ASYNC - as async functions always return a promise, breaking read-after-write consistency for clients that have already loaded the cache.
 */
export const getItemCategoryMaybeSync = (
	itemKey: string,
	storageType: CategoryStorageType,
	productName?: string,
): Promise<PreferenceCategory | null> | PreferenceCategory | null => {
	const storageControlsDataOrPromise = getBscGlobalState().loadCacheMaybeSync(storageType);

	if (isPromise<CategorizedStorageControlsCache | undefined>(storageControlsDataOrPromise)) {
		return storageControlsDataOrPromise.then((storageControlsData) => {
			if (!storageControlsData) {
				return null;
			} else {
				return getItemCategoryFromPassedCache(itemKey, storageControlsData, productName);
			}
		});
	}
	if (!storageControlsDataOrPromise) {
		return null;
	} else {
		return getItemCategoryFromPassedCache(itemKey, storageControlsDataOrPromise, productName);
	}
};

function isProcessingAllowedForCategoryAndPreferenceSync(
	category: PreferenceCategory | null,
	preferences: ConsentPreference | undefined,
): boolean {
	if (!!preferences) {
		if (!!category) {
			return preferences[category];
		}
	}
	return false;
}

/**
 * Given an item category and a promise or object of user preferences, determines whether the item can be set.
 * It avoids only returns a promise if we need the preference data to determine the result, otherwise it returns synchronously.
 *
 * DO NOT CONVERT THIS FUNCTION TO ASYNC - as async functions always return a promise, breaking read-after-write consistency for clients that have already loaded the cache.
 */
function isProcessingAllowedForCategoryMaybeSync(
	itemCategory: PreferenceCategory | null,
	preferencesOrPromise: ConsentPreference | undefined | Promise<ConsentPreference | undefined>,
): Promise<CategoryAndPreference> | CategoryAndPreference {
	if (itemCategory === PreferenceCategory.StrictlyNecessary) {
		return {
			itemCategory,
			shouldProcessItem: true,
		};
	}

	if (isPromise(preferencesOrPromise)) {
		return preferencesOrPromise.then((prefs) => {
			return {
				itemCategory,
				shouldProcessItem: isProcessingAllowedForCategoryAndPreferenceSync(itemCategory, prefs),
			};
		});
	} else {
		return {
			itemCategory,
			shouldProcessItem: isProcessingAllowedForCategoryAndPreferenceSync(
				itemCategory,
				preferencesOrPromise,
			),
		};
	}
}

const getProcessingAllowedByPreferencesAndCategoryMaybeSync = ({
	itemKey,
	storageType,
	productName,
}: {
	itemKey: string;
	storageType: CategoryStorageType;
	productName?: string;
}): Promise<CategoryAndPreference> | CategoryAndPreference => {
	// start fetching both promises upfront, so they can resolve in parallel if needed
	const itemCategoryOrPromise = getItemCategoryMaybeSync(itemKey, storageType, productName);
	const preferencesOrPromise = getPreferencesMaybeSync();

	if (isPromise<PreferenceCategory | null>(itemCategoryOrPromise)) {
		return itemCategoryOrPromise.then((category) =>
			isProcessingAllowedForCategoryMaybeSync(category, preferencesOrPromise),
		);
	} else {
		return isProcessingAllowedForCategoryMaybeSync(itemCategoryOrPromise, preferencesOrPromise);
	}
};

export const isProcessingAllowedByPreferencesMaybeSync = ({
	itemKey,
	storageType,
	productName,
}: {
	itemKey: string;
	storageType: CategoryStorageType;
	productName?: string;
}): Promise<boolean> | boolean => {
	const categoryAndPreferenceOrPromise = getProcessingAllowedByPreferencesAndCategoryMaybeSync({
		itemKey,
		storageType,
		productName,
	});

	if (isPromise<CategoryAndPreference>(categoryAndPreferenceOrPromise)) {
		return categoryAndPreferenceOrPromise.then(({ shouldProcessItem }) => shouldProcessItem);
	} else {
		return categoryAndPreferenceOrPromise.shouldProcessItem;
	}
};

/**
 * Given an item key (WebStorage key or cookie name), fetches preferences and categories and determines whether the key can be set
 *
 * Allows an optional allowedCallback to be passed which is invoked in the case the cookie is allowed to be set.
 * This allows synchronous parts of the code to check a item's status (e.g. the localStorage or document.cookie overrides)
 *
 */
export const isProcessingAllowedByPreferences = async ({
	itemKey,
	allowedCallback = () => {},
	blockedCallback = () => {},
	storageType,
	productName,
}: {
	itemKey: string;
	allowedCallback?: ({ wasAsync }: { wasAsync: boolean }) => void;
	blockedCallback?: ({
		itemHasCategory,
		itemCategory,
		wasAsync,
	}: {
		itemHasCategory?: boolean;
		itemCategory?: PreferenceCategory | null;
		wasAsync: boolean;
	}) => void;
	storageType: CategoryStorageType;
	productName?: string;
}): Promise<boolean> => {
	let shouldProcessItem;
	let itemCategory: PreferenceCategory | null = null;
	let wasAsync: boolean = true;
	if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
		let categoryAndPreferenceOrPromise = getProcessingAllowedByPreferencesAndCategoryMaybeSync({
			itemKey,
			storageType,
			productName,
		});
		if (isPromise(categoryAndPreferenceOrPromise)) {
			// this should be the only await in the function.
			({ shouldProcessItem, itemCategory } = await categoryAndPreferenceOrPromise);
		} else {
			wasAsync = false;
			({ shouldProcessItem, itemCategory } = categoryAndPreferenceOrPromise);
		}
	} else {
		itemCategory = await getItemCategory(itemKey, storageType, productName);

		if (itemCategory === PreferenceCategory.StrictlyNecessary) {
			shouldProcessItem = true;
		} else {
			const preferences = await getPreferences();
			let hasPreferencesForCategory = false;

			if (!!preferences) {
				if (!!itemCategory) {
					hasPreferencesForCategory = preferences[itemCategory];
				}
			}
			shouldProcessItem = hasPreferencesForCategory;
		}
	}
	if (shouldProcessItem) {
		allowedCallback?.({ wasAsync });
	} else {
		blockedCallback?.({
			itemHasCategory: itemCategory != null,
			itemCategory,
			wasAsync,
		});
	}
	return shouldProcessItem;
};

export const isProcessingAllowedByPreferencesWithCategory = async ({
	itemKey,
	storageType,
	productName,
}: {
	itemKey: string;
	storageType: CategoryStorageType;
	productName?: string;
}): Promise<{ isAllowed: boolean; itemCategory: PreferenceCategory | null | undefined }> => {
	const categoryAndPreferenceOrPromise = getProcessingAllowedByPreferencesAndCategoryMaybeSync({
		itemKey,
		storageType,
		productName,
	});

	let shouldProcessItem: boolean;
	let itemCategory: PreferenceCategory | null;

	if (isPromise<CategoryAndPreference>(categoryAndPreferenceOrPromise)) {
		({ shouldProcessItem, itemCategory } = await categoryAndPreferenceOrPromise);
	} else {
		({ shouldProcessItem, itemCategory } = categoryAndPreferenceOrPromise);
	}

	return { isAllowed: shouldProcessItem, itemCategory };
};
